import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BASE } from '../api/client';
import {
  useCharacterCreatorCatalog,
  useCharacterLines,
  useCreateCharacterLine,
  useDeleteCharacterLine,
  useElevenlabsVoices,
  useLipsyncComposite,
  useLipsyncJobStatus,
  usePlayCharacterLine,
  type CharacterCommonLine,
  type CharacterEntry,
  type EmotionOption,
  type LipsyncModelOption,
} from '../hooks/useApi';
import { AssetReferenceInput } from './AssetReferenceInput';

/**
 * D2 — LivePreview Modal: biblioteca de frases pregrabadas con voice override
 * vía audio overlay (no regenera lipsync caro).
 *
 * Estrategia (decisión usuario 2026-04-26):
 *  - El VIDEO lipsync se genera UNA vez con la voz default del personaje (~$0.13 con
 *    Kling). Vive cacheado para siempre.
 *  - Para "probar la frase con otra voz", NO regeneramos Kling. Generamos solo el
 *    AUDIO con la voz nueva (gratis/barato con plan ElevenLabs) y al reproducir
 *    muteamos el video + reproducimos el audio overlay sincronizado.
 *  - El lip-sync no va a coincidir 100% con la voz nueva pero el costo baja de
 *    $0.13 a ~$0 por cambio de voz. Ideal para iteración rápida.
 *
 * UI: 1 solo botón ▶ Play por frase. Si hay voz override seteada, automáticamente
 * dispara el flow audio-overlay.
 */
export function LivePreviewModal({
  character,
  onClose,
}: {
  character: CharacterEntry;
  onClose: () => void;
}) {
  const { data: lines } = useCharacterLines(character.id);
  const { data: catalog } = useCharacterCreatorCatalog();
  const { data: voices } = useElevenlabsVoices();
  const playMutation = usePlayCharacterLine(character.id);
  const deleteMutation = useDeleteCharacterLine(character.id);

  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [activeLineId, setActiveLineId] = useState<string | null>(null);
  const [activeVideoAssetId, setActiveVideoAssetId] = useState<string | null>(null);
  const [activeOverrideAudioId, setActiveOverrideAudioId] = useState<string | null>(null);
  const [voiceOverride, setVoiceOverride] = useState<string>('');
  const [showNewForm, setShowNewForm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Lipsync composite (modo low-cost — avatar chico sobre fondo grande)
  const [compositeEnabled, setCompositeEnabled] = useState(false);
  const [compositeBackgroundId, setCompositeBackgroundId] = useState('');
  const [compositePosition, setCompositePosition] = useState<'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' | 'center'>('bottom-right');
  const [compositeScale, setCompositeScale] = useState(0.30);
  const compositeMutation = useLipsyncComposite(character.id);

  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  // ESC cierra
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Polling del job activo (cuando hay que generar video lipsync nuevo)
  const jobQuery = useLipsyncJobStatus(activeJobId);
  useEffect(() => {
    const data = jobQuery.data;
    if (!data) return;
    if (data.status === 'completed') {
      if (data.videoAssetId) {
        setActiveVideoAssetId(data.videoAssetId);
        // El video se encarga del play (autoPlay) — no manuallemos acá.
      }
      setActiveJobId(null);
    } else if (data.status === 'failed') {
      setError(data.errorMessage ?? 'Lipsync falló — revisá logs.');
      setActiveJobId(null);
    }
  }, [jobQuery.data]);

  /**
   * Reproducir una frase. Flow:
   *  1. Asegurar el video (siempre con voz default) — la mayoría está cacheado.
   *  2. Si voiceOverride está seteado y NO coincide con la voz default del personaje:
   *     generar (o cache HIT) el audio con la voz override (mode='audio', rápido).
   *  3. Setear los IDs activos. PlayerPanel renderiza video (muted si hay override) +
   *     audio overlay sincronizado con events del video.
   */
  const playLine = async (line: CharacterCommonLine) => {
    setError(null);
    setActiveLineId(line.id);
    setActiveJobId(null);
    setActiveVideoAssetId(null);
    setActiveOverrideAudioId(null);

    const useOverride = !!voiceOverride && voiceOverride !== character.voiceId;

    // Modo composite: si está habilitado y hay backgroundId, usar el endpoint composite
    if (compositeEnabled && compositeBackgroundId.trim()) {
      try {
        const result = await compositeMutation.mutateAsync({
          text: line.text,
          backgroundAssetId: compositeBackgroundId.trim(),
          position: compositePosition,
          scale: compositeScale,
          emotion: line.emotion ?? 'neutral',
          voiceOverride: useOverride ? voiceOverride : undefined,
        });
        setActiveVideoAssetId(result.compositeAssetId);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
      return;
    }

    try {
      // 1) Video con voz default (Kling, cache HIT casi siempre)
      const videoResult = await playMutation.mutateAsync({
        lineId: line.id,
        mode: 'video',
        voiceOverride: undefined,
      });

      if (videoResult.status === 'completed' && videoResult.videoAssetId) {
        setActiveVideoAssetId(videoResult.videoAssetId);
      } else if (videoResult.status === 'pending') {
        setActiveJobId(videoResult.jobId);
        // No seguimos al overlay hasta que termine el video
        return;
      } else {
        setError('No se pudo cargar el video');
        return;
      }

      // 2) Si hay voice override, generar/traer el audio con esa voz
      if (useOverride) {
        const audioResult = await playMutation.mutateAsync({
          lineId: line.id,
          mode: 'audio',
          voiceOverride,
        });
        if (audioResult.status === 'completed' && audioResult.audioAssetId) {
          setActiveOverrideAudioId(audioResult.audioAssetId);
        } else {
          setError('No se pudo generar el audio con la voz override');
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  // Sincronización audio overlay ↔ video. Cuando hay overrideAudioId, el video va
  // muted y el audio se reproduce en paralelo. Manejamos play/pause/seek/end del
  // video para que el audio siga.
  useEffect(() => {
    const v = videoRef.current;
    const a = audioRef.current;
    if (!v || !a || !activeOverrideAudioId) return;

    const onPlay = () => { a.play().catch(() => {}); };
    const onPause = () => { a.pause(); };
    const onSeeked = () => { a.currentTime = v.currentTime; };
    const onEnded = () => { a.pause(); a.currentTime = 0; };
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    v.addEventListener('seeked', onSeeked);
    v.addEventListener('ended', onEnded);
    return () => {
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('seeked', onSeeked);
      v.removeEventListener('ended', onEnded);
    };
  }, [activeOverrideAudioId, activeVideoAssetId]);

  const isLoading = playMutation.isPending || compositeMutation.isPending || activeJobId !== null;
  const isPoolA = !character.voiceProvider || character.voiceProvider === 'elevenlabs';
  const overrideActive = !!voiceOverride && voiceOverride !== character.voiceId;
  const overrideVoiceLabel = useMemo(() => {
    if (!overrideActive) return null;
    return voices?.find((v) => v.voice_id === voiceOverride)?.name ?? voiceOverride.slice(0, 8);
  }, [overrideActive, voices, voiceOverride]);

  return createPortal(
    <div
      className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-slate-900 border border-slate-700 rounded-lg w-full max-w-5xl flex flex-col h-[95dvh] sm:h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-4 py-3 border-b border-slate-800 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-lg">🎬</span>
            <h2 className="text-base font-semibold text-slate-100 truncate">
              {character.name} — Biblioteca
            </h2>
            <span className={`text-[10px] px-2 py-0.5 rounded shrink-0 ${isPoolA ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'}`}>
              {isPoolA ? '🟢 Pool A' : '🟡 Pool B'}
            </span>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200 text-lg leading-none">
            ✕
          </button>
        </header>

        {/* Selector de voz override */}
        <div className="px-4 py-2 border-b border-slate-800 shrink-0 flex items-center gap-2 bg-slate-900/50">
          <label className="text-xs text-slate-400 shrink-0">🎙 Voz:</label>
          <select
            value={voiceOverride}
            onChange={(e) => setVoiceOverride(e.target.value)}
            className="flex-1 bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-slate-100 focus:outline-none focus:border-vision min-w-0"
          >
            <option value="">
              Default del personaje
              {character.voiceId ? ` (${voices?.find((v) => v.voice_id === character.voiceId)?.name ?? character.voiceId.slice(0, 8)})` : ''}
            </option>
            <optgroup label="🎭 Voces clonadas">
              {voices?.filter((v) => v.isCloned).map((v) => (
                <option key={v.voice_id} value={v.voice_id}>{v.name}</option>
              ))}
            </optgroup>
            <optgroup label="📚 Voces públicas">
              {voices?.filter((v) => !v.isCloned).map((v) => (
                <option key={v.voice_id} value={v.voice_id}>{v.name}</option>
              ))}
            </optgroup>
          </select>
          {overrideActive && (
            <span className="text-[10px] text-amber-400/80 shrink-0" title="El video original se mutea y se superpone esta voz nueva — sin regenerar lipsync">
              ↻ overlay
            </span>
          )}
        </div>

        {/* Toggle Composite — avatar chico sobre fondo grande (low-cost) */}
        <div className="px-4 py-2 border-b border-slate-800 shrink-0 bg-slate-900/30">
          <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
            <input
              type="checkbox"
              checked={compositeEnabled}
              onChange={(e) => setCompositeEnabled(e.target.checked)}
              className="accent-vision"
            />
            🖼 Componer con fondo (low-cost ~50% más barato)
          </label>
          {compositeEnabled && (
            <div className="mt-2 space-y-2 pl-5">
              <AssetReferenceInput
                value={compositeBackgroundId}
                onChange={setCompositeBackgroundId}
                label="Imagen de fondo"
              />
              <div className="flex items-center gap-2">
                <label className="text-[10px] text-slate-400 shrink-0 w-20">Posición:</label>
                <select
                  value={compositePosition}
                  onChange={(e) => setCompositePosition(e.target.value as typeof compositePosition)}
                  className="flex-1 bg-slate-950 border border-slate-700 rounded px-2 py-1 text-[11px] text-slate-100"
                >
                  <option value="bottom-right">↘ inferior derecha</option>
                  <option value="bottom-left">↙ inferior izquierda</option>
                  <option value="top-right">↗ superior derecha</option>
                  <option value="top-left">↖ superior izquierda</option>
                  <option value="center">⊙ centro</option>
                </select>
                <label className="text-[10px] text-slate-400 shrink-0 ml-2">Escala:</label>
                <span className="text-[10px] text-slate-300 w-12 text-center">
                  {(compositeScale * 100).toFixed(0)}%
                </span>
                <input
                  type="range"
                  min={15}
                  max={50}
                  value={Math.round(compositeScale * 100)}
                  onChange={(e) => setCompositeScale(parseInt(e.target.value, 10) / 100)}
                  className="w-24"
                />
              </div>
              {compositeEnabled && !compositeBackgroundId.trim() && (
                <div className="text-[10px] text-amber-400/80">
                  ⚠ Pegá un asset ID de imagen para que el botón Play use el modo composite.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[18rem_1fr] flex-1 overflow-hidden">
          {/* Panel izquierdo: lista de frases */}
          <aside className="border-r border-slate-800 overflow-y-auto p-3 space-y-2">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs uppercase text-slate-400">📋 Frases ({lines?.length ?? 0})</h3>
              <button
                onClick={() => setShowNewForm((s) => !s)}
                className="text-xs px-2 py-1 rounded bg-vision-dark hover:bg-vision text-white"
              >
                {showNewForm ? '✕' : '+ Nueva'}
              </button>
            </div>

            {!lines || lines.length === 0 ? (
              <div className="text-xs text-slate-500 italic p-4 text-center">
                Sin frases todavía.
              </div>
            ) : (
              lines.map((line) => (
                <LineCard
                  key={line.id}
                  line={line}
                  active={activeLineId === line.id}
                  loading={activeLineId === line.id && isLoading}
                  overrideActive={overrideActive}
                  onPlay={() => playLine(line)}
                  onDelete={() => {
                    if (confirm(`¿Borrar "${line.text.slice(0, 40)}…"?`)) {
                      deleteMutation.mutate(line.id);
                      if (activeLineId === line.id) {
                        setActiveLineId(null);
                        setActiveVideoAssetId(null);
                        setActiveOverrideAudioId(null);
                      }
                    }
                  }}
                  catalog={catalog}
                />
              ))
            )}
          </aside>

          {/* Panel derecho: player o form */}
          <section className="overflow-y-auto p-4 flex flex-col">
            {showNewForm ? (
              <NewLineForm
                characterId={character.id}
                catalog={catalog}
                onCreated={() => setShowNewForm(false)}
                onCancel={() => setShowNewForm(false)}
              />
            ) : (
              <PlayerPanel
                videoAssetId={activeVideoAssetId}
                overrideAudioAssetId={activeOverrideAudioId}
                isLoading={isLoading}
                error={error}
                overrideVoiceLabel={overrideVoiceLabel}
                characterAssetId={character.assetId}
                defaultVideoAssetId={
                  lines?.find((l) => l.lipsyncAssetId)?.lipsyncAssetId ?? null
                }
                videoRef={videoRef}
                audioRef={audioRef}
              />
            )}
          </section>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function PlayerPanel({
  videoAssetId,
  overrideAudioAssetId,
  isLoading,
  error,
  overrideVoiceLabel,
  characterAssetId,
  defaultVideoAssetId,
  videoRef,
  audioRef,
}: {
  videoAssetId: string | null;
  overrideAudioAssetId: string | null;
  isLoading: boolean;
  error: string | null;
  overrideVoiceLabel: string | null;
  characterAssetId: string;
  defaultVideoAssetId: string | null;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  audioRef: React.RefObject<HTMLAudioElement | null>;
}) {
  const characterImg = `${BASE}/assets/${characterAssetId}/thumb`;
  // Mientras nadie clickeó: preview con el primer video disponible.
  const showingVideoId = videoAssetId ?? defaultVideoAssetId;
  const isPreview = videoAssetId === null && defaultVideoAssetId !== null;
  // El video va muteado cuando hay overlay de audio override (la voz "real" la pone
  // el <audio> en paralelo).
  const muted = !!overrideAudioAssetId;

  return (
    <div className="flex flex-col h-full gap-3">
      <div className="flex-1 flex items-center justify-center bg-slate-950 rounded border border-slate-800 min-h-[24rem] overflow-hidden relative">
        {showingVideoId ? (
          <video
            ref={videoRef}
            key={`${showingVideoId}-${overrideAudioAssetId ?? 'none'}`}
            controls
            preload="auto"
            poster={characterImg}
            muted={muted}
            autoPlay={!isPreview}
            src={`${BASE}/assets/${showingVideoId}/proxy`}
            className="w-full h-full object-contain"
          />
        ) : (
          <img
            src={characterImg}
            alt=""
            className="w-full h-full object-contain"
          />
        )}

        {/* Audio overlay invisible — solo se escucha. Se sincroniza con el video
            por los event listeners del modal padre. */}
        {overrideAudioAssetId && (
          <audio
            ref={audioRef}
            preload="auto"
            src={`${BASE}/assets/${overrideAudioAssetId}/proxy`}
          />
        )}

        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-950/60 backdrop-blur-sm">
            <div className="text-center text-slate-200 bg-slate-900/80 rounded-lg px-5 py-4 border border-slate-700">
              <div className="text-3xl mb-2 animate-pulse">⏳</div>
              <div className="text-sm">Cargando…</div>
              <div className="text-[10px] text-slate-400 mt-1">
                Audio ~2s · Video lipsync (si hace falta) ~125s
              </div>
            </div>
          </div>
        )}

        {isPreview && !isLoading && (
          <div className="absolute top-2 right-2 text-[10px] text-slate-300 bg-slate-950/80 rounded px-2 py-1 border border-slate-700">
            Preview · elegí frase y apretá ▶
          </div>
        )}

        {overrideAudioAssetId && !isLoading && (
          <div className="absolute top-2 left-2 text-[10px] text-amber-300 bg-slate-950/80 rounded px-2 py-1 border border-amber-500/30" title={`El video va muteado y la voz "${overrideVoiceLabel}" se reproduce superpuesta`}>
            🎙 voz: {overrideVoiceLabel}
          </div>
        )}
      </div>

      {error && (
        <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">
          {error}
        </div>
      )}

      <div className="text-[10px] text-slate-500 leading-relaxed">
        🟢 Cache HIT = gratis · Voz override usa overlay sin regenerar Kling (~$0)
      </div>
    </div>
  );
}

function LineCard({
  line,
  active,
  loading,
  overrideActive,
  onPlay,
  onDelete,
  catalog,
}: {
  line: CharacterCommonLine;
  active: boolean;
  loading: boolean;
  overrideActive: boolean;
  onPlay: () => void;
  onDelete: () => void;
  catalog: { emotions: EmotionOption[]; lipsyncModels: LipsyncModelOption[] } | undefined;
}) {
  const emoji = catalog?.emotions.find((e) => e.key === line.emotion)?.emoji ?? '😐';
  const statusBadge =
    line.status === 'ready' ? '🟢' : line.status === 'audio-only' ? '🟡' : '⚪';
  const statusLabel = overrideActive
    ? `Voz custom — el video original se mutea, el audio nuevo se overlapa (~$0 por voz)`
    : line.status === 'ready'
      ? 'Audio + video listos · click para reproducir'
      : line.status === 'audio-only'
        ? 'Audio listo, falta generar video (~$0.13 primer click)'
        : 'Sin generar — primer click pregenera (~$0.13)';

  return (
    <div
      className={`p-2 rounded border text-sm transition-colors ${
        active ? 'border-vision bg-vision/10' : 'border-slate-800 hover:border-slate-700 bg-slate-950'
      }`}
    >
      <div className="flex items-start gap-1.5 mb-2">
        <span className="text-base">{emoji}</span>
        <span className="text-[10px] text-slate-500 mt-0.5" title={statusLabel}>{statusBadge}</span>
        <p className="text-xs text-slate-200 leading-snug flex-1 line-clamp-3">{line.text}</p>
        <button
          onClick={onDelete}
          className="text-slate-600 hover:text-red-400 text-xs"
          title="Borrar frase"
        >
          🗑
        </button>
      </div>
      <button
        onClick={onPlay}
        disabled={loading}
        className="w-full px-2 py-1.5 text-xs rounded bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-slate-100 border border-slate-700 hover:border-slate-600 disabled:opacity-40 disabled:cursor-not-allowed"
        title={statusLabel}
      >
        {loading ? '⏳ Cargando…' : '▶ Reproducir'}
      </button>
    </div>
  );
}

function NewLineForm({
  characterId,
  catalog,
  onCreated,
  onCancel,
}: {
  characterId: string;
  catalog: { emotions: EmotionOption[]; lipsyncModels: LipsyncModelOption[] } | undefined;
  onCreated: () => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState('');
  const [emotion, setEmotion] = useState('neutral');
  const [model, setModel] = useState('kling-avatar-v2-standard');
  const createMutation = useCreateCharacterLine(characterId);

  const submit = async () => {
    if (!text.trim()) return;
    try {
      await createMutation.mutateAsync({
        text: text.trim(),
        emotion,
        lipsyncModel: model,
      });
      onCreated();
    } catch {
      // mostrado por createMutation.error
    }
  };

  const selectedModel = useMemo(
    () => catalog?.lipsyncModels.find((m) => m.key === model),
    [catalog, model],
  );
  const estCost = selectedModel
    ? selectedModel.pricing.type === 'flat'
      ? `$${selectedModel.pricing.usd.toFixed(2)}`
      : `~$${(Math.max(2, Math.ceil(text.length / 13)) * selectedModel.pricing.usd).toFixed(2)}`
    : '?';

  return (
    <div className="space-y-3 h-full">
      <h3 className="text-xs uppercase text-slate-400">+ Nueva frase</h3>

      <div>
        <label className="text-xs text-slate-400 block mb-1">Texto</label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-vision resize-none"
          placeholder="Lo que va a decir el personaje…"
        />
        <div className="text-[10px] text-slate-500 mt-1">{text.length} caracteres</div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-slate-400 block mb-1">Emoción</label>
          <select
            value={emotion}
            onChange={(e) => setEmotion(e.target.value)}
            className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-100"
          >
            {catalog?.emotions.map((e) => (
              <option key={e.key} value={e.key}>
                {e.emoji} {e.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-400 block mb-1">Modelo</label>
          <select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-100"
          >
            {catalog?.lipsyncModels.map((m) => (
              <option key={m.key} value={m.key}>{m.label}</option>
            ))}
          </select>
        </div>
      </div>

      {selectedModel && (
        <div className="text-[10px] text-slate-500 bg-slate-950 border border-slate-800 rounded p-2 space-y-1">
          <div>💰 Estimado: {estCost} (1 sola vez, después cache)</div>
          <div>⏱ ~{selectedModel.estimatedJobSeconds.min}-{selectedModel.estimatedJobSeconds.max}s</div>
          <div className="italic text-slate-600">{selectedModel.notes}</div>
        </div>
      )}

      {createMutation.error && (
        <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">
          {(createMutation.error as Error).message}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-sm rounded bg-slate-800 hover:bg-slate-700 text-slate-300"
        >
          Cancelar
        </button>
        <button
          onClick={submit}
          disabled={createMutation.isPending || text.trim().length === 0}
          className="px-3 py-1.5 text-sm rounded bg-vision-dark hover:bg-vision text-white disabled:opacity-40"
        >
          {createMutation.isPending ? 'Creando…' : 'Crear frase'}
        </button>
      </div>
      <div className="text-[10px] text-slate-600 italic">
        Audio + video se generan al primer click sobre la frase. Después quedan cacheados.
      </div>
    </div>
  );
}
